import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Star, Clock, Users } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a coach or consultant invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A coach or consultant invoice should include: your name or business name; your address and contact details; client name and address; invoice number and date; payment due date; a description of each session or service with the rate; subtotal, any applicable tax, and the total amount due; and your preferred payment method (bank transfer, PayPal, Stripe link, etc.).',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I invoice clients for coaching sessions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Add each coaching session as a line item with a description (e.g. "1:1 coaching session, 60 min"), quantity (number of sessions), and your per-session rate. Invilic calculates the total automatically. You can also add a single line item for a package or retainer fee.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a retainer invoice for consulting?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Add a single line item such as "Monthly consulting retainer - May" with your flat retainer fee. You can add additional lines for any out-of-scope work billed separately. Download the PDF and send it to your client.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do coaches need to charge VAT or GST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It depends on your country and annual turnover. In the UK, you must register for VAT if your taxable turnover exceeds £90,000. In India, GST registration is required above ₹20 lakh for services. In Australia, GST applies above AUD $75,000. Below these thresholds, a standard invoice without tax is sufficient.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this coach invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic offers a free coach and consultant invoice template you can fill in online and download as a professional PDF. No sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const billingStyles = [
  {
    icon: Clock,
    title: 'Per session',
    example: '"1:1 coaching session, 60 min x 4 = $800"',
    desc: 'List each session as a line item with quantity and per-session rate.',
  },
  {
    icon: Star,
    title: 'Package pricing',
    example: '"3-month transformation program: $2,400"',
    desc: 'One line item at a flat package rate. Simple and clean.',
  },
  {
    icon: Users,
    title: 'Monthly retainer',
    example: '"Advisory retainer, May 2025: $1,500"',
    desc: 'Recurring invoice with a single retainer line item per month.',
  },
];

export default function CoachInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Coach Invoice Template: Free PDF for Coaches & Consultants | Invilic"
        description="Free coach and consultant invoice template. Bill by session, package, or retainer. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/coach-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Coach & Consultant Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for coaches, consultants, and trainers. Bill by the session, package, or monthly retainer. Fill in online and download a PDF instantly for free.
          </p>
          <Link
            to="/create/coach"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Billing styles */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Three Ways to Bill Your Clients
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {billingStyles.map(({ icon: Icon, title, example, desc }) => (
              <div key={title} className="p-6 rounded-2xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-xs font-mono text-violet-700 dark:text-violet-300 bg-white dark:bg-gray-900 rounded-lg px-3 py-2 mb-3 border border-violet-100 dark:border-violet-800/30">
                  {example}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Template fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Coach Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name or business name & logo',
              'Client name and address',
              'Invoice number and date',
              'Payment due date',
              'Session / service description per line',
              'Quantity (sessions) and rate',
              'Line item totals calculated automatically',
              'Subtotal, discount and tax',
              'Total amount due',
              'Payment terms and bank / payment link details',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Use the Coach Invoice Template
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click the button above. No download or account needed.' },
              { n: '2', title: 'Add your sessions', desc: 'Enter your details, client info, and services. Takes 2 minutes.' },
              { n: '3', title: 'Download PDF', desc: 'Get a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-violet-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who uses this */}
        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5 text-center">
            Who Uses This Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Life and career coaches billing 1:1 clients',
              'Business consultants on project or retainer',
              'Executive coaches invoicing corporate clients',
              'Fitness and wellness coaches',
              'Online course creators billing enrollments',
              'Management consultants on advisory engagements',
            ].map(item => (
              <div key={item} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                <CheckCircle className="w-4 h-4 text-violet-500 flex-shrink-0" /> {item}
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Coach Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Coach Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/coach"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Coach Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
